import React from 'react';
import { UniversalLink } from '@plone/volto/components';
import { formatDate, resolveContentImage } from '../helpers';

import VodButton from '../../../../shared/VodButton';
import {
  getContentPreviousByCurrentContent,
  type PreviousContentItem,
} from '../../../../../services/api/contentPrevious';

type ContentAsideProgramHistoryProps = {
  content?: any;
};

function resolveProgramUrl(content: any): string {
  if (content?.parent?.['@id']) {
    return content.parent['@id'];
  }

  const breadcrumbs = Array.isArray(content?.breadcrumbs)
    ? content.breadcrumbs
    : [];

  if (breadcrumbs.length >= 2) {
    const currentUrl = content?.['@id'] || '';
    const previousCrumb = [...breadcrumbs]
      .reverse()
      .find((item: any) => item?.url && item.url !== currentUrl);

    if (previousCrumb?.url) {
      return previousCrumb.url;
    }
  }

  return '';
}

export default function ContentAsideProgramHistory({
  content,
}: ContentAsideProgramHistoryProps) {
  const [items, setItems] = React.useState<PreviousContentItem[]>([]);
  const [loading, setLoading] = React.useState(true);

  const contentPath = content?.['@id'] || '';
  const programTitle = content?.parent?.title || '';
  const programUrl = resolveProgramUrl(content);

  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!contentPath) {
        setItems([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const result = await getContentPreviousByCurrentContent(contentPath);

        if (cancelled) return;

        setItems((result || []).slice(0, 3));
      } catch (error) {
        if (!cancelled) {
          setItems([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [contentPath]);

  if (loading) {
    return (
      <section className="content-view__aside-section content-view__aside-section--history">
        {programUrl ? (
          <UniversalLink
            href={programUrl}
            className="content-view__aside-section-head content-view__aside-section-head--link"
            title={`Ver mais conteúdos do programa ${programTitle}`}
            aria-label={`Ver mais conteúdos do programa ${programTitle}`}
          >
            <h2 className="content-view__aside-title">Veja mais do Programa</h2>
            <span className="content-view__aside-plus" aria-hidden="true">
              ›
            </span>
          </UniversalLink>
        ) : (
          <div className="content-view__aside-section-head">
            <h2 className="content-view__aside-title">Veja mais do Programa</h2>
            <span className="content-view__aside-plus" aria-hidden="true">
              ›
            </span>
          </div>
        )}

        <p className="content-view__aside-text">Carregando conteúdos...</p>
      </section>
    );
  }

  if (!items.length) {
    return null;
  }

  return (
    <section className="content-view__aside-section content-view__aside-section--history">
      {programUrl ? (
        <UniversalLink
          href={programUrl}
          className="content-view__aside-section-head content-view__aside-section-head--link"
          title={`Ver mais conteúdos do programa ${programTitle}`}
          aria-label={`Ver mais conteúdos do programa ${programTitle}`}
        >
          <h2 className="content-view__aside-title">Veja mais do Programa</h2>
          <span className="content-view__aside-plus" aria-hidden="true">
            ›
          </span>
        </UniversalLink>
      ) : (
        <div className="content-view__aside-section-head">
          <h2 className="content-view__aside-title">Veja mais do Programa</h2>
          <span className="content-view__aside-plus" aria-hidden="true">
            ›
          </span>
        </div>
      )}

      <div className="content-view__aside-list">
        {items.map((item) => {
          const title = item?.title || '';
          const url = item?.['@id'] || '';
          const audioUrl = item?.audio || '';
          const dateAiring = item?.dateAiring || '';
          const formattedDate = formatDate(dateAiring);
          const image = resolveContentImage(item);

          return (
            <article
              key={item?.['@id'] || title}
              className="content-view__aside-item content-view__aside-item--history"
            >
              {programTitle ? (
                <p className="content-view__aside-item-program">
                  {programTitle}
                </p>
              ) : null}

              <UniversalLink
                href={url}
                className="content-view__aside-item-link"
                title={title}
              >
                <h3 className="content-view__aside-item-title">{title}</h3>
              </UniversalLink>

              <div className="content-view__aside-item-meta">
                {formattedDate ? (
                  <p className="content-view__aside-item-date">
                    <time dateTime={dateAiring || undefined}>
                      {formattedDate}
                    </time>
                  </p>
                ) : null}

                <div className="content-view__aside-item-actions">
                  {audioUrl ? (
                    <VodButton
                      url={audioUrl}
                      pageUrl={url}
                      title={title}
                      subtitle={programTitle}
                      image={image}
                      label="Ouça agora"
                      origin="ContentAsideProgramHistory"
                      analytics={{
                        event: 'audio_play',
                        section: 'content.aside.programHistory',
                        contentId: url,
                        contentType: 'vod',
                        label: 'Ouça agora',
                      }}
                      className="content-view__aside-play pressable"
                    />
                  ) : (
                    <UniversalLink
                      href={url}
                      className="content-view__aside-read-more"
                      title={title}
                    >
                      Ver conteúdo
                    </UniversalLink>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {programUrl ? (
        <UniversalLink
          href={programUrl}
          className="content-view__aside-more"
          title={`Ver mais de ${programTitle}`}
        >
          Ver mais
        </UniversalLink>
      ) : null}
    </section>
  );
}
